/**
 * @fileoverview Inline vibe preview thumbnails.
 *               Shows 3 sample images (a, b, c) when a vibe is selected.
 *               Click a thumbnail to expand it inline; click again to collapse.
 *               Used by VisualGenerationPanel and VideoGenerationPanel.
 */

import React, { useState, useCallback } from 'react';

const SUFFIXES = ['a', 'b', 'c'];

/**
 * Inline preview thumbnails for a vibe.
 * @param {string} vibeId — e.g. "moody_drama"
 * @param {"image"|"video"} type — determines folder path
 * @param {boolean} compact — smaller thumbnails for narrow sidebars (video panel)
 */
export default function VibePreview({ vibeId, type = 'image', compact = false }) {
    const [expandedIdx, setExpandedIdx] = useState(null);

    const basePath = `/images/vibes/${type}`;
    const srcs = SUFFIXES.map(s => `${basePath}/${vibeId}_${s}.webp`);

    const handleClick = useCallback((idx, e) => {
        e.stopPropagation();
        setExpandedIdx(prev => prev === idx ? null : idx);
    }, []);

    return (
        <div className={`mt-2 ${compact ? '' : 'mt-3'}`}>
            {/* Expanded image — fills card width, auto height */}
            {expandedIdx !== null && (
                <img
                    src={srcs[expandedIdx]}
                    alt={`${vibeId} preview ${SUFFIXES[expandedIdx]}`}
                    className="w-full rounded-lg mb-1.5 cursor-pointer"
                    onClick={(e) => handleClick(expandedIdx, e)}
                />
            )}

            {/* Thumbnail strip */}
            <div className="flex gap-1.5">
                {srcs.map((src, i) => (
                    <button
                        key={i}
                        onClick={(e) => handleClick(i, e)}
                        className={`relative overflow-hidden rounded-lg border transition-all flex-1 ${expandedIdx === i ? 'border-slate-blue ring-1 ring-slate-blue' : 'border-white/10 hover:border-slate-blue/50'} ${compact ? 'h-[52px]' : 'h-[72px]'}`}
                    >
                        <img
                            src={src}
                            alt={`${vibeId} preview ${SUFFIXES[i]}`}
                            className="w-full h-full object-cover"
                            loading="lazy"
                        />
                    </button>
                ))}
            </div>
        </div>
    );
}
